// File: peers.js
import { getRemoteTrackCallback } from "./signaling";
import { applyBandwidthProfile } from "./bandwidthProfiles";

// choosing bandwidth profile from Guildhall
let currentProfile = "normal";

export function setBandwidthProfile(profile) {
  currentProfile = profile;

  // Apply to all existing peers
  connectedPeers.forEach(({ peerConnection }) => {
    applyBandwidthProfile(peerConnection, profile);
  });
}

const iceConfig = {
  iceServers: [
    { urls: "stun:stun.voipstunt.com" },
    {
      urls: "turn:ca-east.relay.metered.ca:80",
      username: "8b41a86df47debcd4e895ba6",
      credential: "Nw6CJ+RurUrTbIez",
    },
  ],
};

// 🔥 Store peer connection details
export const connectedPeers = new Map();
// 🔥 Store the local media stream promise
let localStreamPromise = null;
// 🔌 Socket reference, injected from outside
let socket = null;

export const setSocketForPeers = (s) => {
  socket = s;
};

export const setLocalStreamPromise = (promise) => {
  localStreamPromise = promise;
};

export function updatePeers(clientList) {
  if (!socket) {
    console.error("⚠️ updatePeers called before socket was set!");
    return;
  }

  // 🔍 Normalize to just socket IDs
  const normalizedPeers = clientList.map(p =>
    typeof p === "string" ? p : p.socketId
  );

  const currentPeers = new Set(connectedPeers.keys());
  const newPeers = new Set(normalizedPeers);

  newPeers.forEach((peerId) => {
    if (!currentPeers.has(peerId) && peerId !== socket.id) {
      addPeerForClient(peerId);

      localStreamPromise?.then((stream) => {
        if (stream) {
          const peerConnection = connectedPeers.get(peerId)?.peerConnection;
          if (!peerConnection) {
            console.warn(`⚠️ No peerConnection found for ${peerId}`);
            return;
          }

          let added = false;

        }
      });
    }
  });

  currentPeers.forEach((peerId) => {
    if (!newPeers.has(peerId) && connectedPeers.has(peerId)) {
      connectedPeers.get(peerId)?.peerConnection?.close();
      connectedPeers.delete(peerId);
    }
  });
}


export async function addPeerForClient(peerInfo) {
  if (!socket) {
    console.error("⚠️ addPeerForClient called before socket was set!");
    return;
  }

  const peerId = typeof peerInfo === "string" ? peerInfo : peerInfo.socketId;

  if (!peerId) {
    console.error("❌ Invalid peerInfo passed to addPeerForClient:", peerInfo);
    return;
  }

  if (!connectedPeers.has(peerId)) {
    addPeer(peerId);
  } else {
    console.warn(`⚠️ Peer connection already exists for ${peerId}, skipping creation.`);
  }
}

async function addPeer(peerInfo) {
  const peerId = typeof peerInfo === "string" ? peerInfo : peerInfo.socketId;

  if (!peerId) {
    console.error("❌ Invalid peerId passed to addPeer:", peerInfo);
    return;
  }

  const peerConnection = new RTCPeerConnection(iceConfig);
  peerConnection.pendingIceCandidates = [];

  peerConnection.onsignalingstatechange = () => {
    console.log(`🔄 Signaling state for ${peerId}:`, peerConnection.signalingState);
  };

  peerConnection.oniceconnectionstatechange = () => {
    console.log(`🌐 ICE connection state for ${peerId}:`, peerConnection.iceConnectionState);
  };

  peerConnection._sentCandidates = new Set();

  peerConnection.onicecandidate = (event) => {
    if (!socket) {
      console.error("⚠️ Socket not set—cannot emit ICE candidate.");
      return;
    }

    if (event.candidate && event.candidate.candidate) {
      const candidateKey = event.candidate.candidate;

      if (!peerConnection._sentCandidates.has(candidateKey)) {
        peerConnection._sentCandidates.add(candidateKey);

        socket.emit("signal", {
          type: "iceCandidate",
          target: peerId,
          sender: socket.id,
          candidate: event.candidate,
        });
      } else {
        console.log(`🧊 Skipped duplicate ICE candidate for ${peerId}`);
      }
    } else {
      console.log(`📭 ICE gathering complete for ${peerId}`);
    }
  };

  peerConnection.ontrack = (event) => {
    const callback = getRemoteTrackCallback();
    if (callback) {
      callback(peerId, event.track, event.streams);
    } else {
      console.warn("⚠️ No remote track callback defined!");
    }
  };

  try {
    while (!localStreamPromise || typeof localStreamPromise.then !== "function") {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    const localStream = await localStreamPromise;

    if (localStream) {
  // Add all tracks once
  localStream.getTracks().forEach((track) => {
    peerConnection.addTrack(track, localStream);
  });

  // Apply bandwidth profile AFTER all tracks are added
  applyBandwidthProfile(peerConnection, currentProfile);
} else {
  console.warn("⚠️ Local stream unavailable even after promise resolved.");
}

  } catch (error) {
    console.error("❌ Error resolving local stream for", peerId, error);
  }

  connectedPeers.set(peerId, { peerConnection });
}


export function addIceCandidate(peerId, candidate) {
  const peerData = connectedPeers.get(peerId);
  if (peerData && peerData.peerConnection) {
    peerData.peerConnection
      .addIceCandidate(new RTCIceCandidate(candidate))
      .catch((err) => console.error("⚠️ Error adding ICE candidate:", err));
    console.log(`❄️ ICE candidate added to ${peerId}`);
  } else {
    console.warn(`⚠️ Cannot add ICE candidate—peer connection missing for ${peerId}`);
  }
}

export function isPeerConnected(peerId) {
  return connectedPeers.has(peerId);
}
